// Panneaux du détail d'une entrée : historique des mesures et enregistrements de secours.
import { useCallback, useEffect, useState } from "react";
import { api, unwrap } from "../api/client";
import type { components } from "../api/schema";
import { useCan } from "../lib/auth";
import { formatBitrate, formatBytes, formatNumber, formatTime } from "../lib/format";
import { t } from "../lib/i18n";
import { TimeSeriesChart, type SeriesPoint } from "./TimeSeriesChart";
import { Button, ConfirmDialog, Panel, Segmented, useToast } from "./ui";

type Series = components["schemas"]["MetricsSeries"];
type Recordings = components["schemas"]["RecordingList"];

const RANGES = [
  { value: "60", label: "1 h" }, { value: "360", label: "6 h" },
  { value: "1440", label: "24 h" }, { value: "2880", label: "48 h" },
] as const;
type RangeValue = (typeof RANGES)[number]["value"];

export function HistoryPanel({ inputId }: { inputId: string }) {
  const [range, setRange] = useState<RangeValue>("60");
  const [series, setSeries] = useState<Series | null>(null);
  const [now, setNow] = useState(() => Date.now() / 1000);

  useEffect(() => {
    let alive = true;
    const load = () => {
      unwrap(api.GET("/api/inputs/{input_id}/metrics", { params: { path: { input_id: inputId }, query: { minutes: Number(range) } } }))
        .then((s) => { if (alive) { setSeries(s); setNow(Date.now() / 1000); } })
        .catch(() => alive && setSeries(null));
    };
    load();
    const timer = window.setInterval(load, 30_000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [inputId, range]);

  const from = now - Number(range) * 60;
  const pts = series?.points ?? [];
  const step = series?.bucket_s ?? 10;
  const gap = step * 2.5;
  const bitrate: SeriesPoint[] = pts.map((p) => ({ ts: p.ts, value: p.online ? p.bitrate_kbps : 0, offline: !p.online }));
  const hasSrt = pts.some((p) => p.loss_pct !== null);
  const loss: SeriesPoint[] = pts.map((p) => ({ ts: p.ts, value: p.loss_pct, offline: !p.online }));
  const offlineCount = pts.filter((p) => !p.online).length;
  const worstLoss = Math.max(0, ...pts.map((p) => p.loss_pct ?? 0));

  return (
    <Panel title={t("Historique")}
      actions={<Segmented label={t("Période")} value={range} onChange={setRange} options={RANGES.map((r) => ({ ...r }))} />}>
      <div className="flex flex-col gap-5 p-4">
        <TimeSeriesChart title={t("Débit entrant")} points={bitrate} from={from} to={now} step={step} gapAfter={gap}
          format={(v) => formatBitrate(v)} />
        {hasSrt && (
          <TimeSeriesChart title={t("Pertes SRT (max. par point, %)")} points={loss} from={from} to={now} step={step} gapAfter={gap}
            format={(v) => `${formatNumber(v)} %`} height={130} />
        )}
        <p className="text-[12px] text-muted">
          {pts.length === 0 ? t("Les mesures sont enregistrées toutes les 10 s, conservées 48 h.")
            : <>{t("Un point = {n} s en moyenne.", { n: series?.bucket_s ?? 0 })}{" "}
              {offlineCount > 0 ? <span className="text-err">{offlineCount > 1 ? t("Signal absent sur {n} points (zones rouges).", { n: offlineCount }) : t("Signal absent sur 1 point (zone rouge).")}</span> : t("Signal présent sur toute la période.")}
              {hasSrt && <> {t("Pire perte SRT : {value} %.", { value: formatNumber(worstLoss) })}</>}</>}
        </p>
        {pts.length > 0 && (
          <details className="text-[13px]">
            <summary className="cursor-pointer text-muted hover:text-ink">{t("Voir les valeurs")}</summary>
            <div className="mt-2 max-h-64 overflow-auto rounded border border-line">
              <table className="w-full text-left">
                <thead className="sticky top-0 bg-raised text-[12px] text-faint">
                  <tr><th className="px-3 py-1.5">{t("Heure")}</th><th className="px-3 py-1.5 text-right">{t("Débit")}</th>
                    {hasSrt && <><th className="px-3 py-1.5 text-right">{t("Pertes")}</th><th className="px-3 py-1.5 text-right">RTT</th></>}</tr>
                </thead>
                <tbody className="num divide-y divide-line">
                  {[...pts].reverse().map((p) => (
                    <tr key={p.ts}>
                      <td className="px-3 py-1">{formatTime(p.ts)}</td>
                      <td className="px-3 py-1 text-right">{p.online ? formatBitrate(p.bitrate_kbps) : t("pas de signal")}</td>
                      {hasSrt && <><td className="px-3 py-1 text-right">{formatNumber(p.loss_pct, "%")}</td>
                        <td className="px-3 py-1 text-right">{formatNumber(p.rtt_ms, "ms")}</td></>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>
    </Panel>
  );
}

export function RecordingsPanel({ inputId, record }: { inputId: string; record: boolean }) {
  const can = useCan();
  const toast = useToast();
  const [list, setList] = useState<Recordings | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(() => {
    unwrap(api.GET("/api/inputs/{input_id}/recordings", { params: { path: { input_id: inputId } } }))
      .then(setList).catch(() => setList(null));
  }, [inputId]);
  useEffect(() => {
    load();
    const timer = window.setInterval(load, 15_000);
    return () => window.clearInterval(timer);
  }, [load]);

  if (!record && (list?.files.length ?? 0) === 0) return null;

  const remove = async () => {
    if (!deleting) return;
    try {
      await unwrap(api.DELETE("/api/inputs/{input_id}/recordings/{name}", { params: { path: { input_id: inputId, name: deleting } } }));
      toast(t("Enregistrement supprimé"));
      load();
    } catch (e) {
      toast((e as Error).message, "err");
    }
    setDeleting(null);
  };

  return (
    <Panel title={list ? t("Enregistrements · {size}", { size: formatBytes(list.total_size) }) : t("Enregistrements")}>
      <p className="border-b border-line px-4 py-2 text-[12px] text-muted">
        {record ? t("Enregistrement en cours dès que le signal est présent. Fichiers conservés {hours} h.", { hours: list?.keep_hours ?? "?" })
          : t("Enregistrement désactivé : les fichiers restants seront effacés à la fin de leur durée de conservation.")}
      </p>
      {!list || list.files.length === 0 ? <p className="p-4 text-muted">{t("Aucun fichier pour l'instant.")}</p> : (
        <ul className="max-h-80 divide-y divide-line overflow-y-auto">
          {list.files.map((f, i) => (
            <li key={f.name} className="flex flex-wrap items-center gap-3 px-4 py-2">
              <span className="num min-w-0 flex-1">
                {formatTime(f.start)} → {i === 0 && record ? t("en cours") : formatTime(f.modified)}
              </span>
              <span className="num text-[13px] text-muted">{formatBytes(f.size)}</span>
              {can("operator") && (
                <a className="text-[13px] text-accent hover:underline" download
                  href={`/api/inputs/${encodeURIComponent(inputId)}/recordings/${encodeURIComponent(f.name)}`}>{t("Télécharger")}</a>
              )}
              {can("admin") && <Button size="sm" variant="ghost" onClick={() => setDeleting(f.name)}>{t("Supprimer")}</Button>}
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog open={deleting !== null} title={t("Supprimer l'enregistrement ?")} confirmLabel={t("Supprimer")}
        onCancel={() => setDeleting(null)} onConfirm={remove}>
        {t("Le fichier {name} sera effacé du serveur. Cette action est définitive.", { name: deleting ?? "" })}
      </ConfirmDialog>
    </Panel>
  );
}
